import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'antd-mobile';
import { Alert, Row, Typography, Button as ButtonAntd, Modal, AutoComplete, Spin, Empty, Input, Form } from 'antd';
import { AimOutlined, EnvironmentOutlined, InfoCircleOutlined, LeftOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import useTranslations from '../../../hooks/useTranslations';
import useMapboxMap from '../../../hooks/useMapboxMap';
import useMapboxLocation from '../../../hooks/useMapboxLocation';

const { Text } = Typography;

const ModalStyled = styled(Modal)`
    top: 0 !important;
    padding: 0 !important;
    border-radius: unset !important;
    margin: 0;
    max-width: 100vw !important;
    width: 100vw !important;
    height: 100vh !important;
    top: 0;

    .ant-modal-content {
        height: 100vh !important;
        border-radius: unset !important;
        display: flex;
        flex-direction: column !important;
    }

    .ant-modal-body {
        border-radius: unset !important;
        width: 100% !important;
        flex: 1;
        padding: 0;
        overflow-y: auto;
    }

    @media (min-width: 768px) {
        max-width: 430px !important;
        height: auto;
        width: 100%;

        .ant-modal-content {
            height: auto;
            border-radius: 12px;
        }

        .ant-modal-body {
            height: auto;
        }
    }
`;

const Container = styled.div`
    height: calc(var(--vh, 1vh) * 100 - 215px);
    display: flex;
    flex-direction: column;
    background: white;
`;

const MapWrapper = styled.div`
    height: 100%;
    overflow: hidden;
    position: relative;
`;

function Location({ visible, onCancel, formInstance }) {
    const mapContainerRef = useRef(null);
    const { t } = useTranslations();
    const [formInstanceSearch] = Form.useForm();

    const [placeName, setPlaceName] = useState('');

    const valueLat = Form.useWatch('lat', formInstance);
    const valueLng = Form.useWatch('long', formInstance);
    const valueUserAddress = Form.useWatch('user_address', formInstance);

    const textInputYourLocation = t('InputYourLocation', 'Input Your Location');
    const textConfirmYourLocation = t('ConfirmYourLocation', 'Confirm Your Location');

    const defaultLatLng = useMemo(() => {
        return valueLat && valueLng ? { lng: valueLng, lat: valueLat } : undefined;
    }, [valueLat, valueLng]);

    const { mapRef } = useMapboxMap({
        mapContainerRef,
        draggable: true,
        defaultLatLng,
    });

    useLayoutEffect(() => {
        const setVh = () => {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        };

        setVh();
        window.addEventListener('resize', setVh);

        return () => {
            window.removeEventListener('resize', setVh);
        };
    }, []);

    const { goToMyLocation, handleInputChange, handleSelect, latLong, results, loading, placeName: placeNameGeo } = useMapboxLocation(mapRef);

    useEffect(() => {
        if (!valueUserAddress && !placeName && !placeNameGeo) return;

        formInstanceSearch.setFieldsValue({
            location_name: valueUserAddress || placeName || placeNameGeo,
        });
    }, [formInstanceSearch, valueUserAddress, placeName, placeNameGeo]);

    return (
        <ModalStyled open={visible} onCancel={onCancel} closable={false} footer={null} bodyStyle={{ padding: 0 }} centered destroyOnClose>
            <Container>
                <MapWrapper>
                    <div id="map-container" ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />
                    <div
                        style={{
                            position: 'absolute',
                            right: 0,
                            top: '12px',
                            left: '12px',
                            zIndex: 999,
                        }}
                    >
                        <ButtonAntd onClick={() => onCancel()} style={{ borderRadius: 8, height: 40, width: 40 }} icon={<LeftOutlined />} />
                    </div>

                    <div
                        style={{
                            position: 'absolute',
                            bottom: 24,
                            right: 16,
                            zIndex: 999,
                        }}
                    >
                        <ButtonAntd
                            onClick={() => goToMyLocation()}
                            type="primary"
                            style={{ borderRadius: 8, height: 40, width: 40 }}
                            icon={<AimOutlined />}
                        />
                    </div>

                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(to bottom, #007F78 0%, #007F78 0%, rgba(0, 127, 120, 0%) 30%)',
                            zIndex: 1,
                            pointerEvents: 'none',
                            opacity: 0.6,
                        }}
                    />
                </MapWrapper>
            </Container>
            <div
                style={{
                    boxShadow: '0px 0px 16px rgba(168, 216, 243, 0.5)',
                    background: 'white',
                    borderTopRightRadius: 12,
                    borderTopLeftRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    padding: 16,
                }}
            >
                <Row
                    style={{
                        flexDirection: 'column',
                        rowGap: 16,
                        width: '100%',
                        lineHeight: 1.2,
                    }}
                >
                    <Alert
                        style={{
                            background: '#DFF8F5',
                            padding: '4px 8px',
                            fontSize: 12,
                            marginTop: 8,
                            lineHeight: 1.2,
                        }}
                        description="Please enter your correct location and double-check it before proceeding with your booking."
                        type="info"
                        icon={
                            <InfoCircleOutlined
                                style={{
                                    color: 'var(--primary-color)',
                                    width: 16,
                                    height: 16,
                                }}
                            />
                        }
                        showIcon
                    />
                    <Form form={formInstanceSearch}>
                        <Form.Item name="location_name" style={{ marginBottom: 0 }}>
                            <AutoComplete
                                options={results}
                                onSelect={(value, option) => {
                                    setPlaceName(option?.label);
                                    handleSelect(value);
                                }}
                                onSearch={handleInputChange}
                                notFoundContent={loading ? <Spin /> : <Empty />}
                            >
                                <Input size="large" placeholder={textInputYourLocation} prefix={<EnvironmentOutlined />} />
                            </AutoComplete>
                        </Form.Item>
                    </Form>

                    <Button
                        onClick={() => {
                            formInstance.setFieldsValue({
                                lat: latLong?.[0] || defaultLatLng?.lat,
                                long: latLong?.[1] || defaultLatLng?.lng,
                                user_address: placeName || valueUserAddress || placeNameGeo,
                            });

                            onCancel();
                        }}
                        disabled={(!latLong?.[0] || !latLong?.[1]) && (!defaultLatLng?.lat || !defaultLatLng?.lng)}
                        color="primary"
                        style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                color: 'white',
                                gap: 9,
                            }}
                        >
                            <Text style={{ color: 'white' }}>{textConfirmYourLocation}</Text>
                        </div>
                    </Button>
                </Row>
            </div>
        </ModalStyled>
    );
}

export default Location;
